import { useState, useEffect, useContext, useRef, useCallback } from 'react'

import { TelemetryContext } from '../context/TelemetryContext'

import { GLOBAL_EVENTS } from '../constants/telemetryEvents'

// The width of a column resize handle (workspace.scss .ws-resizer.ws-horizontal).
const RESIZER_PX = 4

// initialSizes (optional): { leftWidth, rightWidth, terminalHeight } in px, for a replay
// shown in a smaller frame than the live workspace.
export function usePanelResize(containerRef, initialSizes = {}) {
  const telemetryContext = useContext(TelemetryContext)
  const logTelemetryEvent = telemetryContext?.logTelemetryEvent || (() => {})

  // Initial sizes
  const [leftWidth, setLeftWidth] = useState(initialSizes.leftWidth ?? 450)
  const [rightWidth, setRightWidth] = useState(initialSizes.rightWidth ?? 450)
  const [terminalHeight, setTerminalHeight] = useState(initialSizes.terminalHeight ?? 250)

  // Mirrors of the sizes above, read inside the drag listeners below so those
  // listeners don't need leftWidth/rightWidth/terminalHeight in their effect
  // deps - without this, every mousemove during a drag (which updates one of
  // those state values) tore down and re-added the document listeners for
  // the rest of the drag, which is what made resizing feel laggy.
  const leftWidthRef = useRef(leftWidth)
  const rightWidthRef = useRef(rightWidth)
  const terminalHeightRef = useRef(terminalHeight)

  // Initial/Final Comparison (CSS variant) IDE/"Your View" split - a percentage rather
  // than a pixel width since that row's own container width varies with the workspace
  // panel widths above. ideSplitContainerRef is set at drag-start (see
  // startDraggingIdeSplit) rather than threaded through as a hook argument, since only
  // that one call site needs it.
  const [ideSplitPercent, setIdeSplitPercent] = useState(50)
  const ideSplitPercentRef = useRef(ideSplitPercent)
  const ideSplitContainerRef = useRef(null)

  // InitialFinalJS's Target/"Your View" split - a vertical (up/down) counterpart to
  // ideSplitPercent above, same percentage-of-container approach and the same reason
  // (the row's own height varies, so a fixed pixel split wouldn't hold up).
  const [jsViewSplitPercent, setJsViewSplitPercent] = useState(50)
  const jsViewSplitPercentRef = useRef(jsViewSplitPercent)
  const jsViewSplitContainerRef = useRef(null)

  // Active drag state
  const [activeResizer, setActiveResizer] = useState(null) // 'left', 'right', 'terminal', 'ideSplit', or 'jsViewSplit'

  useEffect(() => {
    if (!activeResizer) return

    const handleMouseMove = (e) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()

      if (activeResizer === 'left') {
        const newWidth = Math.max(200, Math.min(800, e.clientX - rect.left))
        leftWidthRef.current = newWidth
        setLeftWidth(newWidth)
      }

      if (activeResizer === 'right') {
        const newWidth = Math.max(200, Math.min(800, rect.right - e.clientX))
        rightWidthRef.current = newWidth
        setRightWidth(newWidth)
      }

      if (activeResizer === 'terminal') {
        const newHeight = Math.max(100, Math.min(600, rect.bottom - e.clientY))
        terminalHeightRef.current = newHeight
        setTerminalHeight(newHeight)
      }

      if (activeResizer === 'ideSplit' && ideSplitContainerRef.current) {
        const splitRect = ideSplitContainerRef.current.getBoundingClientRect()
        const percent = ((e.clientX - splitRect.left) / splitRect.width) * 100
        // Clamped well short of 0/100 so neither side of this specific split ever
        // collapses to nothing - unlike left/right above, both sides here are actively
        // used at once (typing CSS, then dragging to check the render), not toggled between.
        const clamped = Math.max(20, Math.min(80, percent))
        ideSplitPercentRef.current = clamped
        setIdeSplitPercent(clamped)
      }

      if (activeResizer === 'jsViewSplit' && jsViewSplitContainerRef.current) {
        const splitRect = jsViewSplitContainerRef.current.getBoundingClientRect()
        const percent = ((e.clientY - splitRect.top) / splitRect.height) * 100
        // Same 20/80 clamp as ideSplit, for the same reason - both Target and Your View
        // stay usable rather than either one collapsing away.
        const clamped = Math.max(20, Math.min(80, percent))
        jsViewSplitPercentRef.current = clamped
        setJsViewSplitPercent(clamped)
      }
    }

    const handleMouseUp = () => {
      // Log the final panel size
      let newSize = null
      if (activeResizer === 'left') newSize = leftWidthRef.current
      if (activeResizer === 'right') newSize = rightWidthRef.current
      if (activeResizer === 'terminal') newSize = terminalHeightRef.current
      if (activeResizer === 'ideSplit') newSize = Math.round(ideSplitPercentRef.current)
      if (activeResizer === 'jsViewSplit') newSize = Math.round(jsViewSplitPercentRef.current)

      logTelemetryEvent(GLOBAL_EVENTS.PANEL_RESIZE, {
        panel: activeResizer,
        newSize: newSize,
        timestamp: Date.now()
      })

      setActiveResizer(null)
      document.body.style.cursor = "default"
      document.body.style.userSelect = "auto"
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    document.body.style.userSelect = "none"

    // Set global cursor while dragging
    if (activeResizer === 'left' || activeResizer === 'right' || activeResizer === 'ideSplit') {
      document.body.style.cursor = "col-resize"
    } else {
      document.body.style.cursor = "row-resize"
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [activeResizer, containerRef, logTelemetryEvent])

  // Makes room for a right column that appears while the page is open (a delayed study
  // condition's AI chat at its unlock): the middle panel keeps at least minMiddle px; the right
  // column gives way first, then the left one, neither below the drag minimum of 200 px.
  const fitRightPanel = useCallback((minMiddle = 360) => {
    const width = containerRef.current?.getBoundingClientRect().width
    if (!width) return
    // The two resize handles between the three panels.
    const room = width - 2 * RESIZER_PX - minMiddle
    let left = leftWidthRef.current
    let right = rightWidthRef.current
    if (left + right <= room) return
    right = Math.max(200, Math.min(right, room - left))
    if (left + right > room) left = Math.max(200, room - right)
    rightWidthRef.current = right
    setRightWidth(right)
    leftWidthRef.current = left
    setLeftWidth(left)
  }, [containerRef])

  return {
    leftWidth,
    rightWidth,
    terminalHeight,
    ideSplitPercent,
    jsViewSplitPercent,
    activeResizer,
    fitRightPanel,
    startDraggingLeft: (e) => { e.preventDefault(); setActiveResizer('left') },
    startDraggingRight: (e) => { e.preventDefault(); setActiveResizer('right') },
    startDraggingTerminal: (e) => { e.preventDefault(); setActiveResizer('terminal') },
    // containerEl: the specific IDE/"Your View" row element (not the whole workspace
    // container), captured at drag-start since it isn't known until that row renders.
    startDraggingIdeSplit: (e, containerEl) => {
      e.preventDefault()
      ideSplitContainerRef.current = containerEl
      setActiveResizer('ideSplit')
    },
    // containerEl: the Target/"Your View" column (not the whole workspace container),
    // same reasoning as startDraggingIdeSplit above.
    startDraggingJsViewSplit: (e, containerEl) => {
      e.preventDefault()
      jsViewSplitContainerRef.current = containerEl
      setActiveResizer('jsViewSplit')
    },
  }
}